<script setup>
import PageHeader from "@/components/ui/PageHeader.vue";
import Calendar from "primevue/calendar";
import Column from "primevue/column";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import Dialog from "primevue/dialog";
import Divider from "primevue/divider";
import Checkbox from "primevue/checkbox";
import Tag from "primevue/tag";
import MapByName from "@/components/MapByName/MapByName.vue";
import TarjetaTerritorio from "@/page/TarjetaTerritorio.vue";
import TerritorioSelector from "@/components/TerritorioSelector.vue";
import GestorTokens from "@/components/GestorTokens.vue";
import AgregarPuntoEncuentroSalida from "@/components/AgregarPuntoEncuentroSalida.vue";
import AgregarGrupoSalida from "@/components/AgregarGrupoSalida.vue";
import SelectorConductorSalida from "@/components/SelectorConductorSalida.vue";
import { useToast } from "primevue/usetoast";
// import { FilterMatchMode, FilterOperator } from "primevue/api";

import { useSalidaStore } from "@/store/useSalidaStore";
import { useStorage } from "@/composables/useStorage";

import { useTrabajoStore } from "@/store/useTrabajoStore";
import { useProximaSalida } from "@/composables/useProximaSalida";
import { useTerritoriosStore } from "@/store/territorios";

import { useTerritorioStore } from "@/store/territorio";
import { useUrl } from "@/composables/useUrl";
import { useRouterStore } from "@/store/router";
import { turnoDeHora } from "@/utils/turno";
import { useSaveSalida } from "@/composables/commands/useSaveSalida";
import { useApi } from "@/composables/useApi";
import { useGruposInvitados } from "@/composables/useGruposInvitados";
import { useEstudios } from "@/composables/useEstudios";
import { useVoluntarios } from "@/composables/useVoluntarios";
import { useSalidaTokens } from "@/composables/useSalidaTokens";
import {
  useMensajePlantilla,
  renderMensajePlantilla,
  DEFAULT_MENSAJE_PLANTILLA,
  nuevaPlantilla,
} from "@/composables/useMensajePlantilla";

import { ref, onMounted, computed, watch, defineProps } from "vue";

const props = defineProps({
  isMobile: Boolean,
});

const route = useRouterStore();
const territorios = useTerritoriosStore();
const { saveAssignments } = useStorage();

const territorio = useTerritorioStore();
const { getFullMapUrl } = useUrl();
const salida = useSalidaStore();
const { execute: saveSalidaCmd } = useSaveSalida();
const api = useApi();
const trabajo = useTrabajoStore();
const activeWorker = ref(null);
const workIndex = ref(null);
const parametrosOk = ref(false);
const isEditing = ref(false);
const editingId = ref(null);
const showDetail = ref(false);
const form = ref({
  type: "default",
});
const showForm = ref(false);
const salidaTipo = [
  { label: "Normal", type: "default" },
  { label: "De Grupo Normal", type: "group" },
  { label: "De Grupo Flexible", type: "flexible" },
  { label: "De Grupo Continua", type: "grupoContinuo" },
];

const headerFilters = ref({});
const viewMode = ref("fecha"); // 'fecha' | 'continua'

const addNuevaSalida = () => {
  showForm.value = true;
  // showSelectTerritorio.value = true;
};

const savingSalida = ref(false);

const { getProximaSalida } = useProximaSalida();

const getDayName = (date) => {
  const days = [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ];
  if (!date.getDay) date = new Date(date);
  return days[date.getDay()];
};

const getLocalDate = (date) => {
  if (!date) return "";
  if (!date.toLocaleDateString) date = new Date(date);
  return date.toLocaleDateString();
};

const notas = ref("");

// ─── Selector de hora (reloj) + horarios más usados ───────────────────────
// form.hour se guarda como string "HH:MM" (formato usado por turnoDeHora y
// por el orden de la tabla), pero se edita con un Calendar timeOnly que
// trabaja con Date. horaSeleccionada hace de puente entre ambos.
const horaSeleccionada = computed({
  get() {
    if (!form.value.hour) return null;
    const [h, m] = form.value.hour.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  },
  set(date) {
    if (!date) {
      form.value.hour = "";
      return;
    }
    const p = (n) => String(n).padStart(2, "0");
    form.value.hour = `${p(date.getHours())}:${p(date.getMinutes())}`;
  },
});

const setHora = (h) => {
  form.value.hour = h;
};

// Horarios más frecuentes en el historial real de salidas, para completar con un click.
const horariosMasUsados = computed(() => {
  const counts = {};
  [...(salida.list.data || []), ...(salida.history || [])].forEach((s) => {
    if (s.hour) counts[s.hour] = (counts[s.hour] || 0) + 1;
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([h]) => h);
});

const conductorName = (val) => {
  if (!val) return "";
  const found = salida.conductores.find((c) => c.id === val);
  return found ? found.name : val; // fallback: val es el nombre (dato viejo)
};

const grupoName = (val) => {
  if (!val) return "";
  const found = territorios.grupos.find((g) => g.id === val);
  return found ? found.name : val;
};

const gruposName = (grupos) => {
  if (!grupos?.length) return "";
  const names = grupos.map((g) => grupoName(g));
  return names.length === 1 ? `Grupo ${names[0]}` : `Grupos ${names.join(", ")}`;
};

// Etiqueta de display que incluye la zona del grupo (ej: "3 — Zona Norte")
const grupoLabel = (val) => {
  const grupo = territorios.grupos.find((g) => g.id === val);
  const name = grupo ? grupo.name : val;
  const zona = grupo?.zonaId
    ? territorios.zonas.find((z) => z.id === grupo.zonaId)?.name
    : null;
  return zona ? `${name} — ${zona}` : name;
};

const gruposLabel = (grupos) => {
  if (!grupos?.length) return "";
  const labels = grupos.map((g) => grupoLabel(g));
  return labels.length === 1 ? `Grupo ${labels[0]}` : `Grupos ${labels.join(", ")}`;
};

// Congregaciones de una salida: derivadas de las zonas de sus grupos
// (cada zona.name representa una congregación). Las salidas sin grupos se
// consideran de la congregación propia.
const congregacionesDeSalida = (item) => {
  const set = new Set();
  (item.grupos || []).forEach((gid) => {
    const grupo = territorios.grupos.find((g) => g.id === gid);
    const zona = grupo?.zonaId
      ? territorios.zonas.find((z) => z.id === grupo.zonaId)
      : null;
    if (zona?.name) set.add(zona.name);
  });
  if (!set.size && territorios.congregacion) set.add(territorios.congregacion);
  return [...set];
};

// Voluntarios asignados a una salida (dato guardado en la propia salida).
const voluntariosDeSalida = (item) => item.voluntarios || [];

const { getGruposInvitados } = useGruposInvitados();
const gruposInvitadosList = ref([]);
const estudiosApi = useEstudios();

// Los grupos de invitados se relacionan a la salida a través de los grupos de
// predicación: se muestran los que pertenecen a alguno de los grupos de la salida.
const gruposInvitadosDeSalida = (grupos) => {
  if (!grupos?.length) return [];
  return gruposInvitadosList.value.filter((gi) =>
    (gi.grupos || []).some((gid) => grupos.includes(gid))
  );
};

// Totales de personas y vehículos sumando todos los grupos de invitados de la salida.
const totalesInvitadosDeSalida = (grupos) =>
  gruposInvitadosDeSalida(grupos).reduce(
    (acc, gi) => ({
      personas: acc.personas + (Number(gi.cantidad) || 0),
      autos: acc.autos + (Number(gi.autos) || 0),
    }),
    { personas: 0, autos: 0 }
  );

// ─── Voluntarios (API externa por fecha) ──────────────────────────────────────
// Se asignan directamente a la salida (form.voluntarios). Se traen de la API por
// la fecha de la salida y se filtran por la congregación de la zona de sus grupos.
const { getVoluntariosDia } = useVoluntarios();
const voluntariosDia = ref([]); // personas de la fecha cargada
const voluntariosCacheFecha = ref(null);
const loadingVoluntarios = ref(false);
const voluntariosError = ref(null);

// 'YYYY-MM-DD' en hora local (evita el corrimiento UTC de toISOString)
const fechaYMD = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d)) return null;
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Zonas referenciadas por los grupos seleccionados de la salida.
const zonasSalida = computed(() => {
  const zonaIds = new Set(
    (form.value.grupos || [])
      .map((gid) => territorios.grupos.find((g) => g.id === gid)?.zonaId)
      .filter(Boolean)
  );
  return territorios.zonas.filter((z) => zonaIds.has(z.id));
});

const norm = (s) => String(s || "").trim().toLowerCase();
const personaKey = (p) => `${norm(p.nombre)}|${norm(p.whatsapp)}`;

// Voluntarios disponibles: congregación ∈ zonas de la salida y turno compatible con la hora.
const voluntariosDisponibles = computed(() => {
  const congregaciones = new Set(zonasSalida.value.map((z) => norm(z.name)));
  if (!congregaciones.size) return [];
  const turno = turnoDeHora(form.value.hour);
  return voluntariosDia.value
    .filter((p) => congregaciones.has(norm(p.congregacion)))
    .filter((p) => !turno || (p.turnos || []).includes(turno))
    .sort((a, b) =>
      norm(a.congregacion).localeCompare(norm(b.congregacion)) ||
      norm(a.rol).localeCompare(norm(b.rol)) ||
      norm(a.nombre).localeCompare(norm(b.nombre))
    );
});

const isVoluntarioAsignado = (p) =>
  (form.value.voluntarios || []).some((v) => personaKey(v) === personaKey(p));

const toggleVoluntario = (p) => {
  const list = form.value.voluntarios || [];
  const key = personaKey(p);
  if (list.some((v) => personaKey(v) === key)) {
    form.value.voluntarios = list.filter((v) => personaKey(v) !== key);
  } else {
    form.value.voluntarios = [
      ...list,
      {
        nombre: p.nombre,
        whatsapp: p.whatsapp,
        rol: p.rol,
        congregacion: p.congregacion,
        turnos: p.turnos || [],
      },
    ];
  }
};

const waLink = (w) => "https://wa.me/" + String(w || "").replace(/\D/g, "");

const cargarVoluntarios = async () => {
  const fecha = form.value.date ? fechaYMD(form.value.date) : null;
  if (!fecha) {
    voluntariosDia.value = [];
    voluntariosCacheFecha.value = null;
    return;
  }
  if (fecha === voluntariosCacheFecha.value) return; // ya cargada
  loadingVoluntarios.value = true;
  voluntariosError.value = null;
  try {
    const res = await getVoluntariosDia(fecha);
    voluntariosDia.value = res.personas || [];
    voluntariosCacheFecha.value = fecha;
  } catch (e) {
    console.error(e);
    voluntariosError.value = "No se pudieron cargar los voluntarios.";
    voluntariosDia.value = [];
  } finally {
    loadingVoluntarios.value = false;
  }
};

// Recargar cuando cambia la fecha o el tipo de la salida (crear o editar).
watch(
  () => [form.value.date, form.value.type],
  () => {
    if (["group", "flexible"].includes(form.value.type)) cargarVoluntarios();
  }
);

const saveFlexible = async () => {
  if (form.value.grupos.length === 0) return;
  if (form.value.territorios.length === 0) return;
  let params = {
    assignedTo: gruposName(form.value.grupos),
    grupos: form.value.grupos,
    territorios: form.value.territorios,
    notas: form.value.notas,
    type: "public",
  };
  territorios.$patch({
    asignados: [
      ...territorios.asignados,
      {
        ...params,
        id: Date.now(),
      },
    ],
  });
  await saveAssignments();
  clearForm();
};

const clearForm = () => {
  form.value = {
    hour: "",
    type: "default",
    puntoEncuentro: "",
    puntoEncuentroTerritorio: null,
    conductor: "",
    territorios: [],
    voluntarios: [],
  };
};

const editSalida = (data) => {
  form.value = {
    ...JSON.parse(JSON.stringify(data)),
    date: data.date
      ? (data.date instanceof Date ? data.date : new Date(data.date))
      : data.date,
  };
  isEditing.value = true;
  editingId.value = data.id;
  showForm.value = true;
};

const save = async () => {
  savingSalida.value = true;

  if (form.value.type === "flexible") {
    saveFlexible();
    savingSalida.value = false;
    return;
  }

  if (form.value.type !== "grupoContinuo" && !form.value.puntoEncuentro?.latlng) {
    savingSalida.value = false;
    return;
  }

  if (isEditing.value) {
    const dateMs =
      form.value.date instanceof Date
        ? form.value.date.getTime()
        : form.value.date;
    const updated = {
      ...JSON.parse(JSON.stringify(form.value)),
      date: dateMs,
    };
    const res = await api.put("/api/salidas/" + editingId.value, updated);
    if (res?.error) {
      console.error(res.error);
      savingSalida.value = false;
      return;
    }
    salida.list.data = salida.list.data.map((item) =>
      item.id === editingId.value ? updated : item
    );
    isEditing.value = false;
    editingId.value = null;
    clearForm();
    savingSalida.value = false;
    return;
  }

  const { error } = await saveSalidaCmd(JSON.parse(JSON.stringify(form.value)));

  if (error) {
    console.error(error);
    savingSalida.value = false;
    return;
  }

  clearForm();
  savingSalida.value = false;
};

const cancel = () => {
  clearForm();
  isEditing.value = false;
  editingId.value = null;
  showForm.value = false;
};

// const onRowReorder = (event) => {
//   salida.list.data = event.value;
// };

const deleteItem = async (data) => {
  const res = await api.del("/api/salidas/" + data.id);
  if (res?.error) {
    console.error(res.error);
    return;
  }
  salida.list.data = salida.list.data.filter((item) => item.id !== data.id);
};
const print = () => {
  window.print();
};

const list = computed(() => {
  const data = JSON.parse(JSON.stringify(salida.list.data || [])).filter(
    (d) => d.type !== "grupoContinuo"
  );

  const dataWithDate = [...data].map((d) => {
    d.date = new Date(d.date);

    return d;
  });
  // order by date and hour
  const result = dataWithDate.sort((a, b) => {
    if (a.date.getTime() === b.date.getTime()) {
      return a.hour.localeCompare(b.hour);
    }
    return a.date.getTime() - b.date.getTime();
  });

  // console.log("list", result);
  return result;
});

// Salidas "De Grupo Continua": sin fecha/hora, se muestran en su propia vista.
const listContinua = computed(() =>
  JSON.parse(JSON.stringify(salida.list.data || [])).filter(
    (d) => d.type === "grupoContinuo"
  )
);

const ready = ref(false);
const showSelectTerritorio = ref(false);

onMounted(() => {
  // last monday

  // verificamos si estan los parametros necesarios
  parametrosOk.value = salida.conductores.length >= 2;

  getGruposInvitados()
    .then((result) => {
      gruposInvitadosList.value = result.guestGroups || [];
    })
    .catch((e) => console.error("Error cargando grupos de invitados", e));

  loadPlantillas();
  loadEnlaces();

  let d = new Date();
  let day = d.getDay(),
    diff = d.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
  let filterStart = new Date(d.setDate(diff));

  headerFilters.value.from = filterStart;

  ready.value = true;
});

const puntosEncuentro = computed(() => {
  // console.log('puntosEncuentro', form.value.territorios);
  let otros = [];
  for (let i in form.value.territorios) {
    let name = form.value.territorios[i];
    // console.log("name", name, territorios.data)
    let info = territorios.data[name];
    if (info?.puntoEncuentro) {
      for (let j in info.puntoEncuentro) {
        let punto = info.puntoEncuentro[j];
        if (!punto.latlng) continue;
        let index = otros.findIndex(
          (o) =>
            o.latlng.lat === punto.latlng.lat &&
            o.latlng.lng === punto.latlng.lng
        );
        if (index === -1) {
          otros.push({
            ...punto,
            index: otros.length + 1,
          });
        }
      }
    }
  }
  // console.log('otros', otros, territorios.puntoEncuentro);
  return [...otros, ...territorios.puntoEncuentro];
});

const onPuntoCreado = (punto) => {
  form.value.puntoEncuentro = punto;
};

const territoriosInfo = ref({});

const refreshTerritorios = () => {
  showSelectTerritorio.value = false;
  for (let i in form.value.territorios) {
    let name = form.value.territorios[i];
    if (territoriosInfo.value[name]?.hasOwnProperty("showMap")) continue;
    territoriosInfo.value[name] = {
      showMap: false,
    };
  }
};

const removeTerritorio = (item) => {
  // console.log("removeTerritorio", item);
  if (territoriosInfo.value[item]) {
    territoriosInfo.value[item].showMap = false;
  }
  form.value.territorios = form.value.territorios.filter((t) => t !== item);
};
const visible = ref(false);
const terrWork = (items) => {
  workIndex.value = null;
  activeWorker.value = items;
  if (items.length === 1) {
    setTerrWork(items[0]);
  }
  visible.value = true;
  // territorio.$patch(item);
};
const getTerritoriosList = (list) => {
  if (!list) return "";
  return list.join(" - ");
};

// Clave de territorio en el formato usado por la salida pública (?territorio=<key>),
// ver getTerrKey en SalidaPublica.vue.
const terrKeyFromName = (name) => {
  const t = territorios.data[name];
  if (!t) return "";
  return `${t.zona || ""}${t.numero || ""}`;
};

const copySalida = (data) => {
  let doc = { ...data, id: Date.now() };
  delete doc.validUntil;
  delete doc.linkId;
  delete doc.mapId;

  // Resolver conductor nombre → ID para que el dropdown pre-seleccione
  if (doc.conductor) {
    const c = salida.conductores.find(
      (c) => c.id === doc.conductor || c.name === doc.conductor
    );
    if (c) doc.conductor = c.id;
  }
  // Resolver grupos nombres → IDs
  if (doc.grupos?.length) {
    doc.grupos = doc.grupos.map((g) => {
      const found = territorios.grupos.find(
        (item) => item.id === g || item.name === g
      );
      return found ? found.id : g;
    });
  }
  form.value = doc;
  showForm.value = true;
};

const setTerrWork = (item) => {
  workIndex.value = null;
  territorio.$patch(territorios.data[item]);
  setTimeout(() => {
    workIndex.value = item;
  }, 400);
};

const showDialogDetail = ref(false);

const ultimasSalidasDelDetalle = computed(() => {
  if (!showDetail.value) return [];
  const todas = [...(salida.list.data || []), ...(salida.history || [])];
  return todas
    .filter((s) => Array.isArray(s.territorios) && s.territorios.includes(showDetail.value))
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 5);
});

const showGestorTokens = ref(false);
const gestorPreselectIds = ref([]);

// ─── Filtro por enlace (token) ─────────────────────────────────────────────
const enlacesList = ref([]);

const loadEnlaces = async () => {
  const { tokens: result = [] } = await getTokens();
  enlacesList.value = result;
};

// ─── Compartir rápido (una sola salida) ───────────────────────────────────
// Botón directo por fila: reutiliza el enlace ya creado para esa salida en
// particular, o crea uno nuevo, y lo copia/comparte sin abrir el gestor.
const toast = useToast();
const { getTokens, createToken, addSalidaToToken, getPublicUrl } = useSalidaTokens();
const sharingSalidaId = ref(null);

// Reutiliza el enlace ya creado para esa salida puntual, o crea uno nuevo.
// Compartido por el botón "Compartir" y por el envío de WhatsApp a invitados.
const getOrCreateSalidaLink = async (data) => {
  const salidaId = String(data.id);
  const { tokens: existing = [] } = await getTokens();
  let token = existing.find(
    (t) => (t.salidaIds || []).length === 1 && String(t.salidaIds[0]) === salidaId
  );

  if (!token) {
    const label =
      gruposLabel(data.grupos) || conductorName(data.conductor) || "Salida continua";
    const created = await createToken(label);
    if (!created?.token) throw new Error(created?.error || "No se pudo crear el enlace");
    await addSalidaToToken(created.token, salidaId);
    token = created;
  }

  return getPublicUrl(token.token);
};

// Abre el enlace público ya generado para esta salida puntual (sin crear uno
// nuevo) centrado en el territorio indicado. Usado desde la columna Territorio.
const abrirTerritorioEnEnlace = async (data, terrName) => {
  const salidaId = String(data.id);
  try {
    const { tokens: existing = [] } = await getTokens();
    const token = existing.find(
      (t) => (t.salidaIds || []).length === 1 && String(t.salidaIds[0]) === salidaId
    );
    if (!token) {
      toast.add({
        severity: "info",
        summary: "Sin enlace",
        detail: "Esta salida todavía no tiene un enlace público generado.",
        life: 3000,
      });
      return;
    }
    const url = new URL(getPublicUrl(token.token));
    url.searchParams.set("salida", salidaId);
    const terrKey = terrKeyFromName(terrName);
    if (terrKey) url.searchParams.set("territorio", terrKey);
    window.open(url.toString(), "_blank", "noopener");
  } catch (e) {
    console.error(e);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "No se pudo abrir el enlace de la salida.",
      life: 4000,
    });
  }
};

const shareSalida = async (data) => {
  sharingSalidaId.value = String(data.id);
  try {
    const url = await getOrCreateSalidaLink(data);

    if (navigator.share) {
      await navigator.share({ title: "Salida de predicación", url });
    } else {
      await navigator.clipboard.writeText(url);
      toast.add({
        severity: "success",
        summary: "Enlace copiado",
        detail: url,
        life: 4000,
      });
    }
  } catch (e) {
    if (e?.name === "AbortError") return; // el usuario canceló el share nativo
    console.error(e);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "No se pudo generar el enlace para compartir.",
      life: 4000,
    });
  } finally {
    sharingSalidaId.value = null;
  }
};

// ─── Mensaje de WhatsApp a un grupo de invitados (por salida) ─────────────
const mensajeApi = useMensajePlantilla();
const plantillas = ref([]);

const loadPlantillas = async () => {
  const result = await mensajeApi.getPlantillas();
  plantillas.value = result.length ? result : [nuevaPlantilla("Predeterminada", DEFAULT_MENSAJE_PLANTILLA)];
};

const zonaNombreDeInvitado = (gi) =>
  (gi.zonaId && territorios.zonas.find((z) => z.id === gi.zonaId)?.name) || "";

// Enlace al punto de encuentro en territorio de la salida (opcional). Apunta a la
// salida pública (no a coordenadas fijas de Google Maps) para que siempre muestre
// el punto vigente, ya que puede cambiar después de enviado el mensaje.
const linkPuntoTerritorio = (data, salidaLink) => {
  if (!data.puntoEncuentroTerritorio?.latlng || !salidaLink) return "";
  const url = new URL(salidaLink);
  url.searchParams.set("salida", String(data.id));
  url.searchParams.set("accion", "pet");
  return url.toString();
};

const showMessageDialog = ref(false);
const messageGrupo = ref(null);
const messageDraft = ref("");
const messageLoading = ref(false);
const selectedPlantillaId = ref(null);
const messageVars = ref(null);

const updateMessageDraft = () => {
  if (!messageVars.value) return;
  const plantilla = plantillas.value.find((p) => p.id === selectedPlantillaId.value);
  messageDraft.value = renderMensajePlantilla(plantilla?.texto, messageVars.value);
};

watch(selectedPlantillaId, updateMessageDraft);

const openMessageDialog = async (gi, data) => {
  if (!gi.whatsapp) return;
  messageGrupo.value = gi;
  messageDraft.value = "";
  selectedPlantillaId.value = plantillas.value[0]?.id || null;
  showMessageDialog.value = true;
  messageLoading.value = true;
  try {
    const link = await getOrCreateSalidaLink(data);
    let estudioLink = { token: null };
    if (gi.id) {
      estudioLink = await estudiosApi.getEstudioLink(gi.id);
      if (!estudioLink.token) estudioLink = await estudiosApi.createEstudioLink(gi.id);
    }
    messageVars.value = {
      codigo: gi.codigo || "",
      encargado: gi.encargado || "",
      cantidad: String(gi.cantidad ?? ""),
      autos: String(gi.autos ?? ""),
      fechas: gi.fechas || "",
      zona: zonaNombreDeInvitado(gi),
      grupos: gruposLabel(data.grupos) || "",
      territorios: (data.territorios || []).join(", "),
      link,
      linkPuntoTerritorio: linkPuntoTerritorio(data, link),
      linkEstudios: estudioLink.token ? estudiosApi.getPublicUrl(estudioLink.token) : "",
    };
    updateMessageDraft();
  } catch (e) {
    console.error(e);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "No se pudo generar el enlace para el mensaje.",
      life: 4000,
    });
  } finally {
    messageLoading.value = false;
  }
};

const sendMessageWhatsapp = () => {
  const url =
    waLink(messageGrupo.value?.whatsapp) + "?text=" + encodeURIComponent(messageDraft.value);
  window.open(url, "_blank", "noopener");
  showMessageDialog.value = false;
};

const salidasParaGestor = computed(() => {
  const source = viewMode.value === "fecha" ? filterList.value : filterListContinua.value;
  return source.map((s) => ({
    id: String(s.id),
    label: s.date
      ? `${getDayName(s.date)} ${getLocalDate(s.date)} ${s.hour || ""}`.trim()
      : gruposLabel(s.grupos) || conductorName(s.conductor) || "Grupo Continua",
  }));
});

const openGestor = (salidaId = null) => {
  if (salidaId) {
    // Enlace para una salida puntual (botón de la fila)
    gestorPreselectIds.value = [String(salidaId)];
  } else {
    // Enlace general: asociar todas las salidas visibles según los filtros activos
    const source = viewMode.value === "fecha" ? filterList.value : filterListContinua.value;
    gestorPreselectIds.value = source.map((s) => String(s.id));
  }
  showGestorTokens.value = true;
};

// trabajo.data is keyed by UUID; resolve via territorios.data[name].uuid
const showDetailTrabajo = computed(() => {
  if (!showDetail.value) return {};
  const uuid = territorios.data[showDetail.value]?.uuid;
  return uuid ? (trabajo.data[uuid] || {}) : {};
});

// Opciones del filtro por congregación: unión de las congregaciones presentes
// en los grupos/zonas de las salidas y en sus voluntarios asignados.
const congregacionOptions = computed(() => {
  const set = new Set();
  [...list.value, ...listContinua.value].forEach((item) => {
    congregacionesDeSalida(item).forEach((c) => set.add(c));
    voluntariosDeSalida(item).forEach((v) => {
      if (v.congregacion) set.add(v.congregacion);
    });
  });
  return [...set].sort((a, b) => a.localeCompare(b));
});

// Salidas incluidas en el enlace seleccionado (por id, comparado como string).
const salidaIdsDelEnlace = computed(() => {
  if (!headerFilters.value.selectedEnlace) return null;
  const token = enlacesList.value.find(
    (t) => t.token === headerFilters.value.selectedEnlace
  );
  return new Set((token?.salidaIds || []).map(String));
});

const filterList = computed(() => {
  const from =
    typeof headerFilters.value.from === "object"
      ? headerFilters.value.from
      : false;
  const to =
    typeof headerFilters.value.to === "object" ? headerFilters.value.to : false;

  let result = [];
  if (from && !to) {
    result = list.value.filter((item) => {
      return item.date >= from.getTime();
    });
  } else if (to && !from) {
    result = list.value.filter((item) => {
      return item.date <= to.getTime();
    });
  } else if (from && to) {
    result = list.value.filter((item) => {
      return item.date >= from.getTime() && item.date <= to.getTime();
    });
  } else {
    result = list.value;
  }

  if (headerFilters.value.selectedGrupo) {
    result = result.filter((item) =>
      item.grupos?.includes(headerFilters.value.selectedGrupo)
    );
  }

  if (headerFilters.value.selectedCongregacion) {
    const target = norm(headerFilters.value.selectedCongregacion);
    result = result.filter((item) => {
      const congs = congregacionesDeSalida(item).map(norm);
      const volCongs = voluntariosDeSalida(item).map((v) => norm(v.congregacion));
      return congs.includes(target) || volCongs.includes(target);
    });
  }

  if (salidaIdsDelEnlace.value) {
    result = result.filter((item) => salidaIdsDelEnlace.value.has(String(item.id)));
  }

  return result;
});

// Filtros de Grupo/Congregación aplicados a las salidas "De Grupo Continua"
// (no tienen fecha, así que no aplica el filtro de rango Desde/Hasta).
const filterListContinua = computed(() => {
  let result = listContinua.value;

  if (headerFilters.value.selectedGrupos?.length) {
    result = result.filter((item) =>
      headerFilters.value.selectedGrupos.some((gid) => item.grupos?.includes(gid))
    );
  }

  if (headerFilters.value.selectedCongregacion) {
    const target = norm(headerFilters.value.selectedCongregacion);
    result = result.filter((item) => {
      const congs = congregacionesDeSalida(item).map(norm);
      const volCongs = voluntariosDeSalida(item).map((v) => norm(v.congregacion));
      return congs.includes(target) || volCongs.includes(target);
    });
  }

  if (salidaIdsDelEnlace.value) {
    result = result.filter((item) => salidaIdsDelEnlace.value.has(String(item.id)));
  }

  return result;
});

const showTerritorio = (k) => {
  showDetail.value = typeof k === "string" ? k : false;
  showDialogDetail.value = true;
};
</script>
<template>
  <div class="salidas-page text-left w-full">
    <div class="col-12 noprint grid ml-0 mb-4 mt-2 salida-form" v-if="showForm">
      <div class="col-12 flex flex-column">
        <h4 class="m-0">{{ isEditing ? 'Editar Salida' : 'Creador de Salidas' }}</h4>
        <div class="text-xs">
          Completa los campos para crear una nueva salida luego presiona el
          botón "Guardar".
        </div>
      </div>
      <div class="col-12 md:col-4">
        <div class="pb-2">Tipo:</div>
        <Dropdown
          v-model="form.type"
          :options="salidaTipo"
          placeholder="Seleccione Tipo"
          optionValue="type"
          optionLabel="label"
          class="w-full"
          data-e2e="dropdown-tipo-salida"
        />
      </div>

      <div class="col-12"></div>
      <div class="col-6 md:col-3" v-if="!['flexible', 'grupoContinuo'].includes(form.type)">
        <div class="pb-2">
          Dia: <span v-if="form?.date">{{ getDayName(form?.date) }}</span>
        </div>
        <Calendar v-model="form.date" class="w-full" dateFormat="dd/mm/yy" />
      </div>
      <div class="col-6 md:col-3" v-if="!['flexible', 'grupoContinuo'].includes(form.type)">
        <div class="pb-2">Hora:</div>
        <div v-if="horariosMasUsados.length" class="flex flex-wrap gap-1 mb-2">
          <Button
            v-for="h in horariosMasUsados"
            :key="h"
            :label="h"
            size="small"
            text
            :outlined="form.hour !== h"
            @click="setHora(h)"
          />
        </div>
        <Calendar
          v-model="horaSeleccionada"
          timeOnly
          hourFormat="24"
          placeholder="Seleccione hora"
          class="w-full"
        />
      </div>

      <div class="col-12 md:col-10 flex flex-column">
        <label class="pb-2" for="">Territorios: </label>
        <div class="flex gap-2">
          <div v-if="form.territorios" class="flex gap-2">
            <div v-for="(item, k) in form.territorios" :key="k">
              <Button
                :label="item"
                severity="secondary"
                outlined
                @click="showTerritorio(item)"
              />
            </div>
          </div>

          <Button
            @click="showSelectTerritorio = true"
            severity="success"
            label="Agregar"
            size="small"
            outlined
          />
        </div>

        <!-- <div>
            <h4>Territorios seleccionados:</h4>
          </div> -->
        <Dialog
          v-model:visible="showDialogDetail"
          maximizable
          modal
          :style="{ width: '90%' }"
          :header="showDetail"
        >
          <div class="w-full">
            <div class="flex justify-content-between flex-column md:flex-row " >
              <div>
                <p class="m-0 text-sm" :class="{
                  'mb-2': props.isMobile
                }">
                  <span class="">
                    {{
                      territorios.data[showDetail].tipo === "default" ||
                      !territorios.data[showDetail].tipo
                        ? "Manzanas"
                        : "Rutas"
                    }}
                    pendientes:
                    {{ showDetailTrabajo.manzanasPendientesTotal }} de
                    {{ showDetailTrabajo.manzanasTotal }}</span
                  >
                </p>
                <p
                  v-if="
                    showDetailTrabajo.manzanasPendientesTotal <
                    showDetailTrabajo.manzanasTotal
                  "
                  class="text-sm m-0"
                >
                  {{ showDetailTrabajo.manzanasPendientes?.join(", ") }}
                </p>
              </div>
              <div class="flex gap-2 mb-2">
                <Button
                  @click="removeTerritorio(showDetail)"
                  label="Quitar territorio"
                  outlined
                  severity="secondary"
                />
                <Button
                  @click="showDialogDetail = false"
                  label="Cerrar"
                  outlined
                  severity="secondary"
                />
              </div>
            </div>
            <div class="mb-3">
              <div class="text-sm font-bold mb-2">Últimas salidas</div>
              <div v-if="ultimasSalidasDelDetalle.length === 0" class="text-sm text-500">
                Sin salidas registradas.
              </div>
              <table v-else class="w-full text-sm">
                <thead>
                  <tr class="text-left border-bottom-1 surface-border">
                    <th class="pb-1 pr-3">Día</th>
                    <th class="pb-1 pr-3">Fecha</th>
                    <th class="pb-1 pr-3">Horario</th>
                    <th class="pb-1">Conductor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="s in ultimasSalidasDelDetalle" :key="s.id" class="border-bottom-1 surface-border">
                    <td class="py-1 pr-3 font-medium">{{ s.date ? getDayName(s.date) : 'Continua' }}</td>
                    <td class="py-1 pr-3">{{ s.date ? getLocalDate(s.date) : '-' }}</td>
                    <td class="py-1 pr-3">{{ s.hour || '-' }}</td>
                    <td class="py-1">{{ conductorName(s.conductor) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <MapByName
              v-if="showDialogDetail"
              :zona="territorios.data[showDetail].zona || ''"
              :numero="territorios.data[showDetail].numero"
              :puntosEncuentro="puntosEncuentro"
            />
          </div>
        </Dialog>
      </div>

      <div class="col-12 md:col-10 grid noprint">
        <div class="col-12 md:col-6 lg:col-4" v-if="form.type !== 'flexible'">
          <div class="pb-2">Punto de Encuentro:</div>
          <Dropdown
            filter
            v-model="form.puntoEncuentro"
            :options="puntosEncuentro"
            option-label="name"
            placeholder="Seleccione punto de encuentro"
            class="w-full"
          >
            <template #option="{ option }">
              <div>
                <div class="flex gap-1">
                  <span>{{ option.name }}</span>
                  <span class="text-xs text-500" v-if="option.index">{{ option.index }}</span>
                </div>
                <div v-if="option.direccion" class="text-xs text-500">{{ option.direccion }}</div>
              </div>
            </template>
          </Dropdown>
          <AgregarPuntoEncuentroSalida
            :territoriosSeleccionados="form.territorios"
            @saved="onPuntoCreado"
          />
        </div>
        <div class="col-12 md:col-6 lg:col-4" v-if="form.type !== 'flexible'">
          <div class="pb-2">Punto de Encuentro en Territorio <span class="text-xs text-500">(opcional)</span>:</div>
          <Dropdown
            filter
            v-model="form.puntoEncuentroTerritorio"
            :options="puntosEncuentro"
            option-label="name"
            placeholder="Sin punto en territorio"
            showClear
            class="w-full"
          >
            <template #option="{ option }">
              <div>
                <div class="flex gap-1">
                  <span>{{ option.name }}</span>
                  <span class="text-xs text-500" v-if="option.index">{{ option.index }}</span>
                </div>
                <div v-if="option.direccion" class="text-xs text-500">{{ option.direccion }}</div>
              </div>
            </template>
          </Dropdown>
        </div>
        <div class="col-12 md:col-6 lg:col-4" v-if="form.type !== 'flexible'">
          <div class="pb-2">Conductor:</div>
          <SelectorConductorSalida v-model="form.conductor" />
        </div>
        <div class="col-12 md:col-6 flex flex-column" v-if="form.type !== 'flexible'">
          <label class="pb-2" for="">Notas: </label>
          <InputText v-model="form.notas" />
        </div>
        <div
          class="col-12 md:col-4 flex flex-column"
          v-if="['group', 'flexible', 'grupoContinuo'].includes(form.type)"
        >
          <label class="pb-2" for="">Grupos: </label>
          <MultiSelect
            v-model="form.grupos"
            :options="territorios.grupos"
            option-label="name"
            option-value="id"
            placeholder="Selecccione grupos"
            display="chip"
            showClear
            class="w-full md:w-20rem"
          />
          <AgregarGrupoSalida
            @saved="(id) => (form.grupos = [...(form.grupos || []), id])"
          />
        </div>
        <div
          class="col-12 md:col-4 flex flex-column"
          v-if="['group', 'flexible', 'grupoContinuo'].includes(form.type) && gruposInvitadosDeSalida(form.grupos).length"
        >
          <label class="pb-2" for="">Grupos de invitados: </label>
          <div class="flex flex-column gap-1">
            <div
              v-for="gi in gruposInvitadosDeSalida(form.grupos)"
              :key="gi.id"
              class="text-sm"
            >
              <span class="font-bold">{{ gi.codigo }}</span>
              <span class="text-xs text-500">
                ({{ gi.cantidad }}) {{ gi.encargado }}
              </span>
            </div>
            <small class="text-500">Se incluyen automáticamente por los grupos de predicación seleccionados.</small>
          </div>
        </div>

        <!-- Voluntarios disponibles (API externa) por fecha + zona/congregación + turno -->
        <div
          class="col-12 flex flex-column"
          v-if="['group', 'flexible'].includes(form.type) && zonasSalida.length"
        >
          <label class="pb-2">
            Voluntarios disponibles
            <span v-if="turnoDeHora(form.hour)" class="text-xs text-500">
              (turno {{ turnoDeHora(form.hour) }})
            </span>
          </label>

          <div v-if="loadingVoluntarios" class="text-sm text-500">
            <i class="pi pi-spin pi-spinner mr-1" /> Cargando voluntarios...
          </div>
          <div v-else-if="voluntariosError" class="text-sm text-red-500">
            {{ voluntariosError }}
          </div>
          <div
            v-else-if="!voluntariosDisponibles.length"
            class="text-sm text-500"
          >
            Sin voluntarios para esta fecha, zona o turno.
          </div>

          <div v-else class="flex flex-column gap-1">
            <div
              v-for="p in voluntariosDisponibles"
              :key="personaKey(p)"
              class="flex align-items-center gap-2 p-1"
              style="border-bottom: 1px solid var(--surface-200)"
            >
              <Checkbox
                :binary="true"
                :modelValue="isVoluntarioAsignado(p)"
                @update:modelValue="toggleVoluntario(p)"
              />
              <div class="flex-1">
                <span class="font-bold">{{ p.nombre }}</span>
                <span class="text-xs text-500 ml-1">
                  · {{ p.congregacion }} · {{ (p.turnos || []).join(", ") }}
                </span>
                <Tag
                  :value="p.rol"
                  :severity="/capit/i.test(p.rol) ? 'warning' : 'info'"
                  class="ml-2"
                  style="font-size: 0.65rem"
                />
              </div>
              <a
                v-if="p.whatsapp"
                :href="waLink(p.whatsapp)"
                target="_blank"
                class="text-sm"
                v-tooltip.top="'WhatsApp'"
              >
                <i class="pi pi-whatsapp" style="color: #25d366" />
              </a>
            </div>
            <small class="text-500 mt-1">
              {{ (form.voluntarios || []).length }} asignado(s) a esta salida.
            </small>
          </div>
        </div>

        <div class="col-12 flex justify-content-between align-items-center">
          <div class="">
            <Button
              :label="isEditing ? 'Guardar cambios' : 'Agregar Salida'"
              data-e2e="btn-guardar-salida"
              @click="save()"
              :disabled="savingSalida"
            />
            <Button
              label="Ocultar"
              class="p-button-secondary noprint ml-2"
              data-e2e="btn-ocultar-form"
              @click="cancel()"
            />
          </div>
        </div>

        <Dialog
          v-model:visible="showSelectTerritorio"
          maximizable
          modal
          :style="{ width: '90%' }"
          header="Seleccionar Territorio"
        >
          <div class="col-12">
            <!-- <div class="pb-2">
              <h4 class="m-0">Seleccionar Territorio</h4>
            </div> -->
            <TerritorioSelector
              v-model="form.territorios"
              @refresh="refreshTerritorios"
            />
          </div>
        </Dialog>
      </div>
    </div>
    <PageHeader
      v-if="!showForm"
      :title="territorios.congregacion || 'Salidas'"
      subtitle="Registro de salidas de predicación"
      class="noprint"
    >
      <template #actions>
        <Button
          type="button"
          icon="pi pi-plus"
          label="Agregar Salida"
          class="noprint"
          data-e2e="btn-nueva-salida"
          @click="addNuevaSalida"
        />
        <Button
          icon="pi pi-link"
          label="Enlaces"
          outlined
          severity="secondary"
          class="noprint"
          @click="openGestor()"
        />
        <Button icon="pi pi-print" text severity="secondary" class="noprint" @click="print" />
      </template>
    </PageHeader>

    <div class="salidas-content" v-if="ready">
      <!-- Banner de congregación (solo print) -->
      <div class="cong-banner onlyprint">
        {{ territorios.congregacion || "Salidas de Predicación" }}
      </div>

      <div class="noprint">
        <div class="flex gap-2 mb-2">
          <Button
            label="Con fecha"
            size="small"
            :outlined="viewMode !== 'fecha'"
            @click="viewMode = 'fecha'"
          />
          <Button
            label="Continuas"
            size="small"
            :outlined="viewMode !== 'continua'"
            @click="viewMode = 'continua'"
          />
        </div>
        <div
          class="flex flex-column justify-content-between p-3 surface-ground"
          style="
            border-width: 0px 1px 0px 1px;
            border-color: var(--separator);
            border-style: solid;
          "
        >
          <div class="flex flex-wrap gap-3 align-items-center">
            <template v-if="viewMode === 'fecha'">
              <div class="pb-1 mt-2">Desde</div>
              <Calendar
                v-model="headerFilters.from"
                :maxDate="headerFilters.to"
                dateFormat="dd/mm/yy"
              />
              <div class="pb-1 mt-2">hasta</div>
              <Calendar
                v-model="headerFilters.to"
                :minDate="headerFilters.from"
                dateFormat="dd/mm/yy"
              />
            </template>
            <Dropdown
              v-if="viewMode === 'fecha'"
              v-model="headerFilters.selectedGrupo"
              :options="territorios.grupos"
              optionLabel="name"
              optionValue="id"
              placeholder="Todos los grupos"
              showClear
            />
            <MultiSelect
              v-else
              v-model="headerFilters.selectedGrupos"
              :options="territorios.grupos"
              optionLabel="name"
              optionValue="id"
              placeholder="Todos los grupos"
              display="chip"
              showClear
              class="w-full md:w-20rem"
            />
            <Dropdown
              v-model="headerFilters.selectedCongregacion"
              :options="congregacionOptions"
              placeholder="Todas las congregaciones"
              showClear
            />
            <Dropdown
              v-model="headerFilters.selectedEnlace"
              :options="enlacesList"
              optionLabel="name"
              optionValue="token"
              placeholder="Todos los enlaces"
              showClear
            />
          </div>
        </div>
      </div>
      <!-- rgb(83 120 181) -->
      <DataTable
        v-if="viewMode === 'fecha'"
        :value="filterList"
        :paginator="filterList.length > 30"
        :rows="30"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        class="p-datatable-customers w-full print-salida"
        showGridlines
        dataKey="id"
        responsiveLayout="scroll"
      >
        <template #empty> Ningun registro encontrado </template>
        <Column
          rowReorder
          headerStyle="width: 3rem"
          headerClass="noprint"
          bodyClass="noprint"
          :reorderableColumn="false"
        />
        <Column
          field="date"
          :header="!props.isMobile ? 'Dia' : 'Salida'"
          dataType="date"
          :styles="{ 'min-width': '12rem' }"
        >
          <template #body="{ data }">
            <div class="flex flex-column">
              <div class="font-bold" style="color: var(--accent)">
                {{ getDayName(data.date) }}
              </div>
              <div class="font-bold mb-2" style="color: var(--accent)" v-if="props.isMobile">
                {{ data.hour }} hs
              </div>
              <div style="font-size: 0.7rem">{{ getLocalDate(data.date) }}</div>
              <div v-if="props.isMobile" class="flex flex-column gap-2 mt-3">
                <div class="flex flex-column ">
                  <a :href="getFullMapUrl(data, 'links')" target="_blank">
                    <div v-if="data.type === 'group'" class="text-xs font-bold">
                      {{ gruposLabel(data.grupos) }}
                    </div>
                    <div class="text-xs">
                      Punto de Encuentro:
                    </div>
                    <div class="font-bold">
                      {{ data.puntoEncuentro.name }}
                    </div>
                   
                    <div
                      class="pt-1"
                      style="font-size: 0.75rem"
                      v-if="data.notas"
                    >
                      {{ data.notas }}
                    </div>
                  </a>
                  <div v-if="data.puntoEncuentroTerritorio?.name" class="mt-1 text-xs text-500">
                    En territorio: {{ data.puntoEncuentroTerritorio.name }}
                  </div>
                </div>
                <div class="flex gap-2 mt-2">
                  <Button
                    type="button"
                    icon="pi pi-pencil"
                    text
                    severity="info"
                    class="p-button-rounded p-button-outlined noprint"
                    @click="editSalida(data)"
                  />
                  <Button
                    type="button"
                    icon="pi pi-clone"
                    tag="Clonar"
                    text
                    class="p-button-rounded p-button-success p-button-outlined noprint"
                    @click="copySalida(data)"
                  />
                  <Button
                    type="button"
                    icon="pi pi-trash"
                    text
                    class="p-button-rounded p-button-danger p-button-outlined"
                    @click="deleteItem(data)"
                  />
                </div>
              </div>
            </div>
          </template>
        </Column>
        <Column
          field="hour"
          header="Hora"
          :styles="{ 'min-width': '12rem' }"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">{{ data.hour }}</template>
        </Column>
        <Column
          field="puntoEncuentro"
          header="Punto de Encuentro"
          :styles="{ 'min-width': '12rem' }"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">
            <!-- PUNTO DE ENCUENTRO -->
            <div class="flex flex-column">
              <a :href="getFullMapUrl(data, 'links')" target="_blank">
                <div v-if="data.type === 'group'" class="text-xs font-bold">
                  Grupo {{ gruposLabel(data.grupos) }}
                </div>
                <div>
                  {{ data.puntoEncuentro.name }}
                </div>
                <div class="pt-1" style="font-size: 0.75rem" v-if="data.notas">
                  {{ data.notas }}
                </div>
              </a>
              <div v-if="data.puntoEncuentroTerritorio?.name" class="mt-1 text-xs text-500">
                En territorio: {{ data.puntoEncuentroTerritorio.name }}
              </div>
              <div v-if="gruposInvitadosDeSalida(data.grupos).length" class="mt-1 text-xs text-500">
                <div
                  v-for="gi in gruposInvitadosDeSalida(data.grupos)"
                  :key="gi.id"
                  :class="{ 'gi-clickable': gi.whatsapp }"
                  v-tooltip="gi.whatsapp ? 'Enviar mensaje por WhatsApp' : null"
                  @click.stop.prevent="openMessageDialog(gi, data)"
                >
                  Invitados: {{ gi.codigo }}<template v-if="gi.encargado"> — {{ gi.encargado }}</template>
                  <template v-if="gi.cantidad"> · {{ gi.cantidad }} persona{{ gi.cantidad !== 1 ? 's' : '' }}</template>
                  <template v-if="gi.autos"> · {{ gi.autos }} vehículo{{ gi.autos !== 1 ? 's' : '' }}</template>
                  <i v-if="gi.whatsapp" class="pi pi-whatsapp ml-1" style="color: #25d366" />
                </div>
                <div v-if="gruposInvitadosDeSalida(data.grupos).length > 1" class="font-bold mt-1">
                  Total: {{ totalesInvitadosDeSalida(data.grupos).personas }} persona{{ totalesInvitadosDeSalida(data.grupos).personas !== 1 ? 's' : '' }}
                  <template v-if="totalesInvitadosDeSalida(data.grupos).autos"> · {{ totalesInvitadosDeSalida(data.grupos).autos }} vehículo{{ totalesInvitadosDeSalida(data.grupos).autos !== 1 ? 's' : '' }}</template>
                </div>
              </div>
            </div>
          </template>
        </Column>
        <Column
          field="territorios"
           :header="!props.isMobile ? 'Territorio' : 'Detalles'"
          :styles="{ 'min-width': '12rem' }"
        >
          <template #body="{ data }">
            <div v-if="props.isMobile" class="flex flex-column mb-3">
              <div class="text-xs">
                Conductor:
              </div>
              <div class="font-bold">
                {{ conductorName(data.conductor) }}
              </div>
            </div>
            <div
              v-if="props.isMobile && voluntariosDeSalida(data).length"
              class="flex flex-column mb-3"
            >
              <div class="text-xs">Voluntarios:</div>
              <div
                v-for="v in voluntariosDeSalida(data)"
                :key="v.nombre + '|' + v.whatsapp"
                class="flex align-items-center gap-1"
              >
                <span class="font-medium">{{ v.nombre }}</span>
                <span v-if="v.congregacion" class="text-xs text-500">
                  · {{ v.congregacion }}
                </span>
                <a
                  v-if="v.whatsapp"
                  :href="waLink(v.whatsapp)"
                  target="_blank"
                  class="noprint"
                  v-tooltip.top="'WhatsApp'"
                >
                  <i class="pi pi-whatsapp" style="color: #25d366" />
                </a>
              </div>
            </div>
            <div class="text-xs" v-if="props.isMobile" >
                Territorio:
              </div>
            <div v-if="data.territorios?.length > 0" class="font-bold">
              <span
                v-for="(t, idx) in data.territorios"
                :key="t"
                class="gi-clickable"
                v-tooltip.top="'Abrir enlace de la salida en este territorio'"
                @click.stop="abrirTerritorioEnEnlace(data, t)"
              >{{ t }}<template v-if="idx < data.territorios.length - 1"> - </template></span>
            </div>
          </template>
        </Column>
        <Column
          field="conductor"
          header="Conductor"
          :styles="{ 'min-width': '12rem' }"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">{{ conductorName(data.conductor) }}</template>
        </Column>

        <Column
          :styles="{ 'min-width': '12rem' }"
          headerClass="noprint"
          bodyClass="noprint"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">
            <div class="flex gap-2">
              <Button
                type="button"
                icon="pi pi-pencil"
                v-tooltip="'Editar'"
                class="p-button-rounded p-button-outlined noprint"
                severity="info"
                @click="editSalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-clone"
                tag="Clonar"
                class="p-button-rounded p-button-success p-button-outlined noprint"
                @click="copySalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-link"
                v-tooltip="'Gestionar enlaces'"
                class="p-button-rounded p-button-outlined noprint"
                severity="secondary"
                @click="openGestor(String(data.id))"
              />
              <Button
                type="button"
                icon="pi pi-trash"
                class="p-button-rounded p-button-danger p-button-outlined"
                @click="deleteItem(data)"
              />
            </div>
          </template>
        </Column>
        <template
          #footer
          v-if="
            territorios.asignados.filter(
              (item) => item.type === 'public' && item.territorios.length > 0
            ).length > 0
          "
        >
          <!-- territorios asignados a grupos (flexible) -->
          <h5 class="mb-2">Territorios asignados a grupos:</h5>
          <div class="print-salida flex gap-2 align-content-between">
            <div
              v-for="(item, k) in territorios.asignados.filter(
                (item) => item.type === 'public' && item.territorios.length > 0
              )"
              class="border-round-md bg-white p-2 w-10rem flex flex-column gap-2"
              :key="k"
            >
              <div>
                <div class="font-normal">{{ item.assignedTo }}</div>
              </div>
              <div v-if="item.territorios?.length > 0">
                <a :href="getFullMapUrl(item)" target="_blank">
                  <div class="flex flex-column justify-content-between">
                    <div>
                      <div class="font-bold">
                        {{ getTerritoriosList(item.territorios) }}
                      </div>
                    </div>
                    <div class="territorio-salida mt-1 font-normal">
                      <span class="text-xs terr">Abrir Mapa</span>
                    </div>
                  </div>
                </a>
              </div>
              <div class="territorio-salida noprint font-normal">
                <a class="terr2" @click="terrWork(item.territorios)"
                  >Registro</a
                >
              </div>
            </div>
          </div>
        </template>

        <template #footer>
          <div
            class="noprint text-sm mt-3"
            v-show="!showForm && list.length > 0"
          >
            <label>Notas (opcional):</label>
            <Textarea v-model="notas" autoResize rows="1" class="w-full mt-1" />
          </div>
        </template>
      </DataTable>

      <!-- Vista "De Grupo Continua": salidas sin fecha/hora -->
      <DataTable
        v-else
        :value="filterListContinua"
        :paginator="filterListContinua.length > 30"
        :rows="30"
        :rowsPerPageOptions="[5, 10, 20, 50]"
        class="p-datatable-customers w-full print-salida"
        showGridlines
        dataKey="id"
        responsiveLayout="scroll"
      >
        <template #empty> Ningun registro encontrado </template>
        <Column
          field="territorios"
          :header="!props.isMobile ? 'Territorio' : 'Detalles'"
          :styles="{ 'min-width': '12rem' }"
        >
          <template #body="{ data }">
            <div v-if="props.isMobile" class="flex flex-column mb-2">
              <div class="text-xs">Conductor:</div>
              <div class="font-bold">{{ conductorName(data.conductor) }}</div>
            </div>
            <div v-if="props.isMobile && gruposLabel(data.grupos)" class="flex flex-column mb-2">
              <div class="text-xs">Grupos:</div>
              <div class="font-bold">{{ gruposLabel(data.grupos) }}</div>
            </div>
            <div
              v-if="props.isMobile && gruposInvitadosDeSalida(data.grupos).length"
              class="flex flex-column mb-2"
            >
              <div class="text-xs">Grupos de invitados:</div>
              <div
                v-for="gi in gruposInvitadosDeSalida(data.grupos)"
                :key="gi.id"
                class="text-sm"
                :class="{ 'gi-clickable': gi.whatsapp }"
                v-tooltip="gi.whatsapp ? 'Enviar mensaje por WhatsApp' : null"
                @click.stop.prevent="openMessageDialog(gi, data)"
              >
                <span class="font-bold">{{ gi.codigo }}</span>
                <template v-if="gi.encargado"> — {{ gi.encargado }}</template>
                <template v-if="gi.fechas"> ({{ gi.fechas }})</template>
                <template v-if="gi.cantidad"> · {{ gi.cantidad }} persona{{ gi.cantidad !== 1 ? 's' : '' }}</template>
                <template v-if="gi.autos"> · {{ gi.autos }} vehículo{{ gi.autos !== 1 ? 's' : '' }}</template>
                <i v-if="gi.whatsapp" class="pi pi-whatsapp ml-1" style="color: #25d366" />
              </div>
              <div v-if="gruposInvitadosDeSalida(data.grupos).length > 1" class="font-bold mt-1">
                Total: {{ totalesInvitadosDeSalida(data.grupos).personas }} persona{{ totalesInvitadosDeSalida(data.grupos).personas !== 1 ? 's' : '' }}
                <template v-if="totalesInvitadosDeSalida(data.grupos).autos"> · {{ totalesInvitadosDeSalida(data.grupos).autos }} vehículo{{ totalesInvitadosDeSalida(data.grupos).autos !== 1 ? 's' : '' }}</template>
              </div>
            </div>
            <div v-if="data.territorios?.length > 0" class="font-bold">
              <span
                v-for="(t, idx) in data.territorios"
                :key="t"
                class="gi-clickable"
                v-tooltip.top="'Abrir enlace de la salida en este territorio'"
                @click.stop="abrirTerritorioEnEnlace(data, t)"
              >{{ t }}<template v-if="idx < data.territorios.length - 1"> - </template></span>
            </div>
            <div v-if="props.isMobile" class="flex gap-2 mt-2">
              <Button
                type="button"
                icon="pi pi-pencil"
                text
                severity="info"
                class="p-button-rounded p-button-outlined noprint"
                @click="editSalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-clone"
                text
                class="p-button-rounded p-button-success p-button-outlined noprint"
                @click="copySalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-share-alt"
                text
                severity="success"
                class="p-button-rounded p-button-outlined noprint"
                v-tooltip="'Compartir enlace'"
                :loading="sharingSalidaId === String(data.id)"
                @click="shareSalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-link"
                text
                severity="secondary"
                class="p-button-rounded p-button-outlined noprint"
                v-tooltip="'Gestionar enlaces'"
                @click="openGestor(String(data.id))"
              />
              <Button
                type="button"
                icon="pi pi-trash"
                text
                class="p-button-rounded p-button-danger p-button-outlined"
                @click="deleteItem(data)"
              />
            </div>
          </template>
        </Column>
        <Column
          field="conductor"
          header="Conductor"
          :styles="{ 'min-width': '10rem' }"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">{{ conductorName(data.conductor) }}</template>
        </Column>
        <Column
          field="grupos"
          header="Grupos"
          :styles="{ 'min-width': '12rem' }"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">
            <div class="flex flex-column">
              <span v-if="gruposLabel(data.grupos)">{{ gruposLabel(data.grupos) }}</span>
              <span v-if="gruposInvitadosDeSalida(data.grupos).length" class="text-xs text-500 mt-1">
                <span
                  v-for="gi in gruposInvitadosDeSalida(data.grupos)"
                  :key="gi.id"
                  class="block"
                  :class="{ 'gi-clickable': gi.whatsapp }"
                  v-tooltip="gi.whatsapp ? 'Enviar mensaje por WhatsApp' : null"
                  @click.stop.prevent="openMessageDialog(gi, data)"
                >
                  Invitados: {{ gi.codigo }}<template v-if="gi.encargado"> — {{ gi.encargado }}</template>
                  <template v-if="gi.fechas"> ({{ gi.fechas }})</template>
                  <template v-if="gi.cantidad"> · {{ gi.cantidad }} persona{{ gi.cantidad !== 1 ? 's' : '' }}</template>
                  <template v-if="gi.autos"> · {{ gi.autos }} vehículo{{ gi.autos !== 1 ? 's' : '' }}</template>
                  <i v-if="gi.whatsapp" class="pi pi-whatsapp ml-1" style="color: #25d366" />
                </span>
                <span v-if="gruposInvitadosDeSalida(data.grupos).length > 1" class="block font-bold text-900 mt-1">
                  Total: {{ totalesInvitadosDeSalida(data.grupos).personas }} persona{{ totalesInvitadosDeSalida(data.grupos).personas !== 1 ? 's' : '' }}
                  <template v-if="totalesInvitadosDeSalida(data.grupos).autos"> · {{ totalesInvitadosDeSalida(data.grupos).autos }} vehículo{{ totalesInvitadosDeSalida(data.grupos).autos !== 1 ? 's' : '' }}</template>
                </span>
              </span>
            </div>
          </template>
        </Column>
        <Column
          field="puntoEncuentro"
          header="Punto de Encuentro"
          :styles="{ 'min-width': '12rem' }"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">
            <a v-if="data.puntoEncuentro?.name" :href="getFullMapUrl(data, 'links')" target="_blank">
              <div>{{ data.puntoEncuentro.name }}</div>
              <div v-if="data.puntoEncuentro.direccion" class="text-xs text-500">
                {{ data.puntoEncuentro.direccion }}
              </div>
            </a>
            <span v-else class="text-xs text-500">Todos los del territorio</span>
          </template>
        </Column>
        <Column
          field="notas"
          header="Notas"
          :styles="{ 'min-width': '10rem' }"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">{{ data.notas }}</template>
        </Column>
        <Column
          :styles="{ 'min-width': '12rem' }"
          headerClass="noprint"
          bodyClass="noprint"
          v-if="!props.isMobile"
        >
          <template #body="{ data }">
            <div class="flex gap-2">
              <Button
                type="button"
                icon="pi pi-pencil"
                v-tooltip="'Editar'"
                class="p-button-rounded p-button-outlined noprint"
                severity="info"
                @click="editSalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-clone"
                class="p-button-rounded p-button-success p-button-outlined noprint"
                @click="copySalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-share-alt"
                v-tooltip="'Compartir enlace'"
                class="p-button-rounded p-button-outlined noprint"
                severity="success"
                :loading="sharingSalidaId === String(data.id)"
                @click="shareSalida(data)"
              />
              <Button
                type="button"
                icon="pi pi-link"
                v-tooltip="'Gestionar enlaces'"
                class="p-button-rounded p-button-outlined noprint"
                severity="secondary"
                @click="openGestor(String(data.id))"
              />
              <Button
                type="button"
                icon="pi pi-trash"
                class="p-button-rounded p-button-danger p-button-outlined"
                @click="deleteItem(data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
      <!-- {{ territorios.asignados.filter((item) => item.type === 'public' && item.territorios.length > 0) }} -->

      <div class="w-full py-2 onlyprint" v-if="notas != ''">{{ notas }}</div>
      <div class="w-full mt-4 onlyprint text-xs">
        En la versión PDF (no en imágenes), toque sobre el punto de encuentro
        para abrir el GPS y sobre el número de territorio para ver el mapa de
        territorio.
      </div>
    </div>
  </div>

  <Dialog
    v-model:visible="visible"
    modal
    :header="parametrosOk ? 'Registro de trabajo' : 'Configuración de inicial'"
    :style="{ width: parametrosOk ? '95vw' : '500px' }"
  >
    <div class="menuWork flex gap-2" v-if="parametrosOk">
      <div
        v-for="(item, index) in activeWorker"
        :key="index"
        class="terr3"
        @click="setTerrWork(item)"
      >
        {{ item }}
      </div>
    </div>
    <div v-else class="line-height-3">
      No tienes configurado los parametros necesarios para trabajar. Por favor,
      primero configura los parametros y luego podrás crear salidas para
      predicar.
      <div class="text-xs mt-2 text-red-500">
        Parametros Obligatorios: Conductores de salidas. Debes tener al menos
        dos conductores cargados.
      </div>
      <Button
        label="Configurar"
        class="p-button-secondary mt-2"
        @click="route.$patch({ page: 'parametros' })"
      />
    </div>
    <Divider />

    <TarjetaTerritorio v-if="workIndex" :isMobile="props.isMobile" />
  </Dialog>

  <GestorTokens
    v-model:visible="showGestorTokens"
    :salidas="salidasParaGestor"
    :preselectSalidaIds="gestorPreselectIds"
  />

  <Dialog
    v-model:visible="showMessageDialog"
    header="Enviar mensaje por WhatsApp"
    modal
    :style="{ width: '480px' }"
  >
    <div class="flex flex-column gap-3 pt-2">
      <div v-if="messageGrupo" class="text-500 text-sm">
        Para {{ messageGrupo.encargado || messageGrupo.codigo }} ({{ messageGrupo.whatsapp }})
      </div>
      <div v-if="plantillas.length" class="flex flex-column gap-1">
        <label class="text-sm">Plantilla</label>
        <Dropdown
          v-model="selectedPlantillaId"
          :options="plantillas"
          option-label="nombre"
          option-value="id"
          class="w-full"
        />
      </div>
      <div v-if="messageLoading" class="text-sm text-500">
        <i class="pi pi-spin pi-spinner mr-1" /> Generando enlace...
      </div>
      <Textarea v-else v-model="messageDraft" rows="8" autoResize />
      <div class="flex gap-2 justify-content-end">
        <Button label="Cancelar" severity="secondary" @click="showMessageDialog = false" />
        <Button
          icon="pi pi-whatsapp"
          label="Enviar por WhatsApp"
          :disabled="messageLoading"
          @click="sendMessageWhatsapp"
        />
      </div>
    </div>
  </Dialog>
</template>
<style scoped>
.gi-clickable {
  cursor: pointer;
}
.gi-clickable:hover {
  text-decoration: underline;
}
.salidas-page { width: 100%; }
.salidas-content { padding: 0 var(--space-4) var(--space-5); }
.cong-banner {
  background: var(--accent);
  color: var(--label-on-accent);
  padding: var(--space-3) var(--space-4);
  font-weight: var(--weight-semibold);
  border-radius: var(--radius-md) var(--radius-md) 0 0;
}
.salida-form {
  background: var(--bg-secondary);
  padding: var(--space-3);
  border-radius: var(--radius-md);
}
.print-salida a,
.print-salida a:visited,
.print-salida a:hover,
.print-salida a:active {
  color: var(--label-primary) !important;
  text-decoration: none !important;
}

a .terr,
a:visited .terr,
a:hover .terr,
a:active .terr {
  border: 1px solid var(--accent) !important;
  background: var(--accent);
  color: #fff !important;
  padding: 0.2rem;
  border-radius: 0.2rem;
}

a.terr2,
a.terr2:visited,
a.terr2:hover,
a.terr2:active {
  border: 1px solid var(--label-tertiary, rgba(60,60,67,.3)) !important;
  background: var(--label-tertiary, rgba(60,60,67,.3));
  color: #fff !important;
  padding: 0.2rem 0.4rem;
  font-size: 0.8rem;
  border-radius: 0.2rem;
  cursor: pointer;
}

.territorio-salida {
  max-width: 200px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  row-gap: 10px;
}

.terr3 {
  border: 1px solid var(--success) !important;
  background: var(--success);
  color: #fff !important;
  padding: 0.5rem;
  border-radius: 0.5rem;
  cursor: pointer;
}
</style>

<style>
/* .noprint {
  display: block !important;
} */
@media print {
  .noprint {
    display: none !important;
  }
}
</style>
